import * as React from "react";
import auth from "../../../Auth";
import WithoutFilterColumnMenu from "../../../Shared/GridComponents/WithoutFilterColumnMenu";
import { CellRender, GridNoRecord,} from "../../../Shared/GridComponents/CommonComponents";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { State, toODataString } from "@progress/kendo-data-query";
import { CustomMenu, DefaultActions, DetailColumnCell, ViewMoreComponent } from "./ShiftGlobalActions";
import {  initialDataState, successToastr, errorToastr, infoToastr, toLocalDateTime } from "../../../../HelperMethods";
import { KendoFilter } from "../../../ReusableComponents";
import { Dialog } from "@progress/kendo-react-dialogs";
import RowActions from "../../../Shared/Workflow/RowActions";
import AddNewShift from "./AddNewShift";
import ConfirmationModal from "../../../Shared/ConfirmationModal";
import { process } from "@progress/kendo-data-query";
import clientAdminService from "../../../Admin/ClientAdmin/Service/DataService";
import { getReqShiftsbyReqIdService, getShiftTypesService } from "./Services";
import { formatTimeForDisplay, generateRecurrenceRule } from './Common/HelperFunctions';
import {REQ_SHIFT_ADDED_SUCCESS_MSG, REQ_SHIFT_UPDATED_SUCCESS_MSG, REQ_SHIFT_DELETED_SUCCESS_MSG, REQ_SHIFT_DELETED_CONFIRM_MSG} from '../../../Shared/AppMessages';
import { LocalizationHelper } from "../../../../Localization/helpers/LocalizationHelper";
import { LocalizationResource } from "../../../Shared/AppConstants";

export interface ManageShiftsProps {
  targetStartDate: any;
  targetEndDate: any;
  positionName?: string;
  positionId?: string;
  capacity?: number;
  reqId?: string;
  locationId?: string;
  locationName?: string;
  departmentId?: string;
  departmentName?: string;
}

export interface ManageShiftsState {
  data: any;
  clientId: string;
  showLoader?: boolean;
  dataItem?: any;
  dataState: any;
  totalCount?: number;
  showEditModal?: boolean;
  showNewShiftModal?: boolean;
  showDeleteModal?: boolean;
  shifts: Array<any>;
  validationError?: string;
  defaultShifts?: Array<any>;
  onFirstLoad?: boolean;
  shiftTypes: Array<{ id: string; name: string }>;
}

class ManageShifts extends React.Component<
ManageShiftsProps,
ManageShiftsState
> {
  public dataItem: any;
  constructor(props: ManageShiftsProps) {
    super(props);
    this.state = {
      data: [],
      clientId: auth.getClient(),
      dataState: initialDataState,
      showLoader: true,
      showNewShiftModal: false,
      showEditModal: false,
      showDeleteModal: false,
      shifts: [],
      validationError: null,
      defaultShifts: [],
      onFirstLoad: true,
      shiftTypes: [],
    };
  }

  componentDidMount() {
    this.getShiftTypes();
    // If we have a reqId, we're in edit mode, so load shifts from the database
    if (this.props.reqId) {
      this.loadShiftsFromDatabase(this.state.dataState);
      return;
    }

    // Only validate required props when not in edit mode
    if (
      !this.props.locationId ||
      !this.props.departmentId ||
      !this.props.positionId
    ) {
      let missingFields = [];
      if (!this.props.locationId) missingFields.push(LocalizationHelper.getLocalizedTitle(LocalizationResource.Location_Label, "Location"));
      if (!this.props.departmentId) missingFields.push(LocalizationHelper.getLocalizedTitle(LocalizationResource.Department_Label, "Department"));
      if (!this.props.positionId) missingFields.push("Position");

      this.setState({
        validationError: `Please select ${missingFields.join(
          ", "
        )} before creating shifts.`,
        showLoader: false,
      });
      return;
    }

    // Load default shifts from the API
    this.fetchDefaultShifts(this.state.dataState);
  }

  componentDidUpdate(prevProps) {
    // Check if any of the key props have changed
    const positionChanged = prevProps.positionId !== this.props.positionId;
    const locationChanged = prevProps.locationId !== this.props.locationId;
    const departmentChanged = prevProps.departmentId !== this.props.departmentId;

    if (locationChanged || departmentChanged || positionChanged) {
      // Clear validation error if we have all required fields
      if (
        this.props.locationId &&
        this.props.departmentId &&
        this.props.positionId
      ) {
        this.setState({ validationError: null });

        // Only reload shifts if we're not in edit mode AND this is not the initial load
        if (!this.props.reqId && !this.state.onFirstLoad) {
          this.reloadDefaultShifts();
        }

        // If this was the first load, set onFirstLoad to false
        if (this.state.onFirstLoad) {
          this.setState({ onFirstLoad: false });
        }
      }
    }
    // Check if position details have changed
    else if (
      prevProps.capacity !== this.props.capacity ||
      prevProps.targetStartDate !== this.props.targetStartDate ||
      prevProps.targetEndDate !== this.props.targetEndDate
    ) {
      // Update existing shifts with new position details
      this.updateShiftsWithPositionDetails(
        this.props.capacity,
        this.props.targetStartDate,
        this.props.targetEndDate
      );
    }
  }

  fetchDefaultShifts = (dataState) => {
    this.setState({ showLoader: true });
    var queryStr = `${toODataString(dataState, { utcDates: true })}`;    
    var queryParams = `locationId eq ${this.props.locationId} and departmentId eq ${this.props.departmentId} and positionId eq ${this.props.positionId}`;
    var finalQueryString = KendoFilter(dataState, queryStr, queryParams);

    clientAdminService
      .getDefaultShifts(finalQueryString)
      .then((res) => {
        // If we have default shifts, process them
        if (res.data.items && res.data.items.length > 0) {
          // Process the default shifts to match our shift format
          const processedShifts = res.data.items.map((defaultShift) => {
            return {
              name:
                defaultShift.shift ||
                defaultShift.name ||
                `Shift ${defaultShift.id}`,
              positionId: this.props.positionId || "",
              positionName: this.props.positionName || "",
              locationId: this.props.locationId || "",
              locationName: this.props.locationName || "",
              departmentId: this.props.departmentId || "",
              departmentName: this.props.departmentName || "",
              capacity: this.props.capacity || 1,
              startTime: defaultShift.startTime,
              endTime: defaultShift.endTime,
              startDate: this.props.targetStartDate
                ? new Date(this.props.targetStartDate)
                : defaultShift.startDate || null,
              endDate: this.props.targetEndDate
                ? new Date(this.props.targetEndDate)
                : defaultShift.endDate || null,
              duration: defaultShift.duration || 8,
              recordStatus: 1,
              reqShiftId_tmp:
                "temp_" +
                new Date().getTime() +
                "_" +
                Math.random().toString(36).substr(2, 9),
              isNewShift: true,
              shiftTypeId:defaultShift.shiftTypeId||null
            };
          });

          // Replace the shifts array instead of appending
          this.setState(
            {
              shifts: processedShifts, // Replace instead of append
              showLoader: false,
            },
            () => {
              // Update the grid with the new shifts
              this.getProcessedShift(this.state.dataState);
            }
          );
        } else {
          // If no default shifts, clear the shifts array
          this.setState(
            {
              shifts: [], // Clear the shifts array
              showLoader: false,
            },
            () => {
              this.getProcessedShift(this.state.dataState);
            }
          );
        }
      })
      .catch((error) => {
        errorToastr("Error loading default shifts");
        this.setState({
          shifts: [], // Clear the shifts array on error
          showLoader: false,
        });
        this.getProcessedShift(this.state.dataState);
      });
  };

  getProcessedShift = (dataState) => {
    this.setState({ showLoader: true });

    // Process the data with the Kendo UI DataQuery
    // Make sure each item has an ID for grid operations
    const processedShifts = this.state.shifts
      // Filter out shifts marked as hidden or deleted
      .filter((shift) => !shift.hidden && !shift.isDeleted)
      .map((shift) => {
        // Create a copy of the shift
        const processedShift = { ...shift };

        // Ensure each item has an ID for grid operations
        // Use reqShiftId_tmp if available, otherwise use reqShiftId
        if (!processedShift.id) {
          processedShift.id =
            processedShift.reqShiftId_tmp || processedShift.reqShiftId;
        }

        return processedShift;
      });

    // Apply the Kendo UI DataQuery operations (sorting, filtering, paging)
    const result = process(processedShifts, dataState);

    this.setState({
      data: result.data,
      dataState: dataState,
      totalCount: result.total,
      showLoader: false,
    });
  };

  onDataStateChange = (changeEvent) => {
    this.getProcessedShift(changeEvent.dataState);
  };

  handleActionClick = (
    action,
    actionId,
    rowId,
    nextStateId?,
    eventName?,
    dataItem?
  ) => {
    this.dataItem = dataItem;

    if (action === "Edit") {
      // Ensure the position ID and name are preserved
      if (!this.dataItem.positionId) {
        this.dataItem.positionId = this.props.positionId || "";
      }

      if (!this.dataItem.positionName) {
        this.dataItem.positionName = this.props.positionName || "";
      }

      // Ensure location ID and name are preserved
      if (!this.dataItem.locationId) {
        this.dataItem.locationId = this.props.locationId || "";
      }

      if (!this.dataItem.locationName) {
        this.dataItem.locationName = this.props.locationName || "";
      }

      // Ensure department ID and name are preserved
      if (!this.dataItem.departmentId) {
        this.dataItem.departmentId = this.props.departmentId || "";
      }

      if (!this.dataItem.departmentName) {
        this.dataItem.departmentName = this.props.departmentName || "";
      }

      // Ensure start date and end date are set if not already
      if (!this.dataItem.startDate && this.props.targetStartDate) {
        this.dataItem.startDate = new Date(this.props.targetStartDate);
      }

      if (!this.dataItem.endDate && this.props.targetEndDate) {
        this.dataItem.endDate = new Date(this.props.targetEndDate);
      }

      // Ensure capacity is set
      if (!this.dataItem.capacity) {
        // Default to props value or 1
        this.dataItem.capacity = this.props.capacity || 1;
      }

      // Make sure we have a valid reqShiftId for editing
      // If we have a real reqShiftId, use it
      if (
        this.dataItem.reqShiftId &&
        !this.dataItem.reqShiftId.toString().startsWith("temp_")
      ) {
      }
      // If we only have a temporary ID, make sure it's preserved for matching
      else if (this.dataItem.reqShiftId_tmp) {
        // If we don't have a real reqShiftId but have a temp one, use an empty string
        // to ensure we don't create a new record with a new temp ID
        if (!this.dataItem.reqShiftId) {
          this.dataItem.reqShiftId = "";
        }
      }

      this.setState({ showEditModal: true });
    } else if (action === "Delete") {
      // Determine which ID to use for deletion
      if (dataItem.reqShiftId_tmp) {
        dataItem.deleteId = dataItem.reqShiftId_tmp;
      } else if (dataItem.reqShiftId) {
        dataItem.deleteId = dataItem.reqShiftId;
      } else {
        dataItem.deleteId = rowId;
      }

      this.setState({ showDeleteModal: true });
    }
  };

  GenerateShiftnModal = () => {
    // Create default start and end times
    const defaultStartTime = new Date(
      new Date(new Date().setHours(9)).setMinutes(0)
    );
    const defaultEndTime = new Date(
      new Date(new Date().setHours(17)).setMinutes(30)
    );

    this.dataItem = {
      name: "",
      positionId: this.props.positionId || "",
      positionName: this.props.positionName || "",
      locationId: this.props.locationId || "",
      locationName: this.props.locationName || "",
      departmentId: this.props.departmentId || "",
      departmentName: this.props.departmentName || "",
      capacity: this.props.capacity || 1,
      duration: 8,
      // Initialize with default times instead of null
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      startDate: this.props.targetStartDate
        ? new Date(this.props.targetStartDate)
        : null,
      endDate: this.props.targetEndDate
        ? new Date(this.props.targetEndDate)
        : null,     
      recurrencePattern: 2,
      recordStatus: 1,
      recurEvery: 1,
      weekDays: {
        sunday: false,
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
      },
      endAfterOccurrences: null,
    };
    this.setState({ showNewShiftModal: true });
  };

  deleteShift = (id) => {
    // Call the removeShift method which now handles on-the-fly shifts
    this.removeShift(id);

    // Close the delete modal
    this.setState({ showDeleteModal: false });
  };

  handleCheckboxChange = (event, dataItem) => {
    const newData = this.state.data.map((item) =>
      item.id === dataItem.id
        ? { ...item, selected: event.target.checked }
        : item
    );
    this.setState({ data: newData });
  };

  addShift = (shiftData: any) => {
    // For new shifts, add a temporary ID for client-side operations
    // but don't use it as reqShiftId which needs to be a valid GUID
    if (!shiftData.reqShiftId) {
      // Add a temporary ID field for client-side operations
      shiftData.reqShiftId_tmp = "temp_" + new Date().getTime();
      // Flag this as a new shift that hasn't been saved to the database yet
      shiftData.isNewShift = true;
    }

    // Add location and department information
    shiftData.locationId = this.props.locationId;
    shiftData.locationName = this.props.locationName;
    shiftData.departmentId = this.props.departmentId;
    shiftData.departmentName = this.props.departmentName;

    // Ensure start date and end date are set if not already
    if (!shiftData.startDate && this.props.targetStartDate) {
      shiftData.startDate = new Date(this.props.targetStartDate);
    }

    if (!shiftData.endDate && this.props.targetEndDate) {
      shiftData.endDate = new Date(this.props.targetEndDate);
    }

    // Make sure startTime and endTime are Date objects
    const newShift = {
      ...shiftData,
      startTime:
        shiftData.startTime instanceof Date
          ? shiftData.startTime
          : new Date(shiftData.startTime),
      endTime:
        shiftData.endTime instanceof Date
          ? shiftData.endTime
          : new Date(shiftData.endTime),
    };

    // First try to find by reqShiftId if it exists and is not empty
    let existingShiftIndex = -1;

    if (newShift.reqShiftId && newShift.reqShiftId !== "") {
      existingShiftIndex = this.state.shifts.findIndex(
        (shift) => shift.reqShiftId && shift.reqShiftId === newShift.reqShiftId
      );
    }

    // If not found by reqShiftId, try to find by reqShiftId_tmp
    if (existingShiftIndex === -1 && newShift.reqShiftId_tmp) {
      existingShiftIndex = this.state.shifts.findIndex(
        (shift) =>
          shift.reqShiftId_tmp &&
          shift.reqShiftId_tmp === newShift.reqShiftId_tmp
      );
    }

    // If we're editing a shift (dataItem is set), try to find it by comparing key properties
    if (existingShiftIndex === -1 && this.dataItem) {
      existingShiftIndex = this.state.shifts.findIndex((shift) => {
        // Try to match by comparing all key properties
        const nameMatch =
          shift.name.toLowerCase() === this.dataItem.name.toLowerCase();

        // Safely check if startTime and endTime are Date objects and have getTime method
        let startTimeMatch = false;
        let endTimeMatch = false;
        let startDateMatch = false;
        let endDateMatch = false;

        try {
          // Ensure both are Date objects before comparing
          if (
            shift.startTime instanceof Date &&
            this.dataItem.startTime instanceof Date
          ) {
            startTimeMatch =
              shift.startTime.getTime() === this.dataItem.startTime.getTime();
          }

          if (
            shift.endTime instanceof Date &&
            this.dataItem.endTime instanceof Date
          ) {
            endTimeMatch =
              shift.endTime.getTime() === this.dataItem.endTime.getTime();
          }

          if (
            shift.startDate instanceof Date &&
            this.dataItem.startDate instanceof Date
          ) {
            startDateMatch =
              shift.startDate.getTime() === this.dataItem.startDate.getTime();
          }

          if (
            shift.endDate instanceof Date &&
            this.dataItem.endDate instanceof Date
          ) {
            endDateMatch =
              shift.endDate.getTime() === this.dataItem.endDate.getTime();
          }
        } catch (error) {
          // If there's an error in comparison, this isn't a match
          return false;
        }
        // Match if name matches AND either time or date pairs match
        return (
          nameMatch &&
          ((startTimeMatch && endTimeMatch) || (startDateMatch && endDateMatch))
        );
      });
    }
    if (existingShiftIndex >= 0) {
      const updatedShifts = [...this.state.shifts];

      // Preserve the reqShiftId and reqShiftId_tmp if they exist in the original shift
      if (updatedShifts[existingShiftIndex].reqShiftId) {
        newShift.reqShiftId = updatedShifts[existingShiftIndex].reqShiftId;
      }
      if (updatedShifts[existingShiftIndex].reqShiftId_tmp) {
        newShift.reqShiftId_tmp =
          updatedShifts[existingShiftIndex].reqShiftId_tmp;
      }

      // If this is a database shift (has a real reqShiftId), mark it as modified
      if (
        updatedShifts[existingShiftIndex].reqShiftId &&
        !updatedShifts[existingShiftIndex].isNewShift
      ) {
        newShift.isModified = true;
      }

      updatedShifts[existingShiftIndex] = newShift;

      this.setState(
        {
          shifts: updatedShifts,
          showEditModal: false,
          showNewShiftModal: false,
        },
        () => {
          this.dataItem = undefined;
          this.getProcessedShift(this.state.dataState);
        }
      );

      successToastr(REQ_SHIFT_UPDATED_SUCCESS_MSG);
    } else {
      this.setState(
        (prevState) => ({
          shifts: [...prevState.shifts, newShift],
          showEditModal: false,
          showNewShiftModal: false,
        }),
        () => {
          this.dataItem = undefined;
          this.getProcessedShift(this.state.dataState);
        }
      );

      successToastr(REQ_SHIFT_ADDED_SUCCESS_MSG);
    }
  };

  removeShift = (reqShiftId: string) => {
    // Find the shift to be removed
    const shiftToRemove = this.state.shifts.find(
      (shift) =>
        (shift.reqShiftId_tmp && shift.reqShiftId_tmp === reqShiftId) ||
        (shift.reqShiftId && shift.reqShiftId === reqShiftId) ||
        (shift.deleteId && shift.deleteId === reqShiftId)
    );

    if (shiftToRemove) {
      // If this is a database shift (has a real reqShiftId and is not a new shift),
      // we need to mark it for deletion rather than removing it from the array
      if (shiftToRemove.reqShiftId && !shiftToRemove.isNewShift) {
        // Create a new array with the shift marked for deletion
        const updatedShifts = this.state.shifts.map((shift) => {
          if (
            (shift.reqShiftId &&
              shift.reqShiftId === shiftToRemove.reqShiftId) ||
            (shift.reqShiftId_tmp &&
              shift.reqShiftId_tmp === shiftToRemove.reqShiftId_tmp)
          ) {
            return {
              ...shift,
              isDeleted: true,
              // Hide it from the grid
              hidden: true,
            };
          }
          return shift;
        });

        this.setState({ shifts: updatedShifts }, () => {
          this.getProcessedShift(this.state.dataState);
        });
      } else {
        // For on-the-fly shifts, just remove them from the array
        this.setState(
          (prevState) => ({
            shifts: prevState.shifts.filter(
              (shift) =>
                !(
                  shift.reqShiftId_tmp && shift.reqShiftId_tmp === reqShiftId
                ) &&
                !(shift.reqShiftId && shift.reqShiftId === reqShiftId) &&
                !(shift.deleteId && shift.deleteId === reqShiftId)
            ),
          }),
          () => {
            this.getProcessedShift(this.state.dataState);
          }
        );
      }

      successToastr(REQ_SHIFT_DELETED_SUCCESS_MSG);
    } else {
      errorToastr("Unable to remove shift");
    }
  };

  getAllShifts = () => {
    // Process all shifts, including those marked for deletion
    const shifts = this.state.shifts.map((shift) => {
      // Create a copy of the shift
      const updatedShift = { ...shift };

      // Remove temporary ID field if it exists
      if (updatedShift.reqShiftId_tmp) {
        delete updatedShift.reqShiftId_tmp;
      }

      // For new shifts, don't include reqShiftId field at all
      if (
        updatedShift.reqShiftId &&
        updatedShift.reqShiftId.toString().startsWith("temp_")
      ) {
        delete updatedShift.reqShiftId;
      }

      // If the shift is marked as deleted, set recordStatus to 2 (deleted)
      if (updatedShift.isDeleted) {
        updatedShift.recordStatus = 2;
      }

      // Remove the client-side tracking flags
      delete updatedShift.isNewShift;
      delete updatedShift.isModified;
      delete updatedShift.hidden;

      // Format dates and times using toLocalDateTime
      if (updatedShift.startDate) {
        updatedShift.startDate = toLocalDateTime(updatedShift.startDate);
      }

      if (updatedShift.endDate) {
        updatedShift.endDate = toLocalDateTime(updatedShift.endDate);
      }

      if (updatedShift.startTime) {
        updatedShift.startTime = toLocalDateTime(updatedShift.startTime);
      }

      if (updatedShift.endTime) {
        updatedShift.endTime = toLocalDateTime(updatedShift.endTime);
      }

      // Ensure reqId is set
      if (this.props.reqId) {
        updatedShift.reqId = this.props.reqId;
      }

      // Ensure recurrenceRule is included
      if (!updatedShift.recurrenceRule) {
        // If no recurrence rule exists, create one based on the pattern
        const pattern = updatedShift.recurrencePattern || 1;
        const recurEvery = updatedShift.recurEvery || 1;
        const weekDays = updatedShift.weekDays || {
          sunday: false,
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
        };

        
// Generate recurrence rule
    const recurrenceRule = generateRecurrenceRule(
      pattern,
      recurEvery,
      weekDays,
      updatedShift.endDate
    );
        updatedShift.recurrenceRule = recurrenceRule;
      }

      return updatedShift;
    });
    return shifts;
  };

  // Load shifts from the database for an existing requisition
  loadShiftsFromDatabase = (dataState) => {
    this.setState({ showLoader: true });
    var queryStr = `${toODataString(dataState, { utcDates: true })}`;
    let queryParams = `reqId eq ${this.props.reqId}`;

    // Add additional filters if available
    if (this.props.locationId) {
      queryParams += ` and locationId eq ${this.props.locationId}`;
    }

    if (this.props.departmentId) {
      queryParams += ` and departmentId eq ${this.props.departmentId}`;
    }

    if (this.props.positionId) {
      queryParams += ` and positionId eq ${this.props.positionId}`;
    }

    // Add status filter
    queryParams += ` and status eq 'Active'&$orderby=position`;

    var finalQueryString = KendoFilter(dataState, queryStr, queryParams);

    // Call the API to get shifts for this requisition 
    getReqShiftsbyReqIdService(this.props.reqId, finalQueryString)
      .then((response) => {
        // If we have shifts from the database, add them to state
        if (
          response.data &&
          Array.isArray(response.data) &&
          response.data.length > 0
        ) {        
          const dbShifts = response.data.map((shift) => {
            // Parse the recurrence rule if it exists
            let weekDays = {
              sunday: false,
              monday: false,
              tuesday: false,
              wednesday: false,
              thursday: false,
              friday: false,
              saturday: false,
            };
            let recurIntev = '';
            let recurrencePat ='';
            if (shift.recurrenceRule) {
              const ruleParts = shift.recurrenceRule.split(";");
              recurrencePat = ruleParts
                .find((part) => part.startsWith("FREQ="))
                ?.split("=")[1];
                recurIntev = ruleParts
                .find((part) => part.startsWith("INTERVAL="))
                ?.split("=")[1];

              const byDay = ruleParts
                .find((part) => part.startsWith("BYDAY="))
                ?.split("=")[1];

              if (byDay) {
                const days = byDay.split(",");
                days.forEach((day) => {
                  switch (day) {
                    case "SU":
                      weekDays.sunday = true;
                      break;
                    case "MO":
                      weekDays.monday = true;
                      break;
                    case "TU":
                      weekDays.tuesday = true;
                      break;
                    case "WE":
                      weekDays.wednesday = true;
                      break;
                    case "TH":
                      weekDays.thursday = true;
                      break;
                    case "FR":
                      weekDays.friday = true;
                      break;
                    case "SA":
                      weekDays.saturday = true;
                      break;
                  }
                });
              }
            }

            // Ensure each shift has the required properties
            return {
              ...shift,
              // Add reqShiftId_tmp for any shifts that don't have a reqShiftId
              reqShiftId_tmp:
                shift.reqShiftId ||
                "temp_db_" +
                  new Date().getTime() +
                  "_" +
                  Math.random().toString(36).substr(2, 9),
              // Add location and department information if not already present
              locationId: shift.locationId || this.props.locationId || "",
              locationName: shift.locationName || this.props.locationName || "",
              departmentId: shift.departmentId || this.props.departmentId || "",
              departmentName:
                shift.departmentName || this.props.departmentName || "",
              // Add position information if not already present
              positionId: shift.positionId || this.props.positionId || "",
              positionName: shift.positionName || this.props.positionName || "",
              // Ensure capacity is set correctly
              capacity: shift.capacity || shift.noOfPositions || 1,
              startTime: new Date(shift.startTime),
              endTime: new Date(shift.endTime),
              // Add recurrence information
              weekDays: weekDays,
              recurrencePattern: (recurrencePat=='DAILY'?1:recurrencePat=='WEEKLY'?2:recurrencePat=='MONTHLY'?3:1), //shift.recurrencePattern || 1,
              recurEvery: recurIntev || 1,
            };
          });

          this.setState({ shifts: dbShifts }, () => {
            this.getProcessedShift(this.state.dataState);
          });
        } 
        else {
          // If no shifts were found in the database, check if we have required props to load default shifts
          if (
            this.props.locationId &&
            this.props.departmentId &&
            this.props.positionId && !this.props.reqId
          ) {
            this.fetchDefaultShifts(this.state.dataState);
          } else {
            this.getProcessedShift(this.state.dataState);
          }
        }
      })
      .catch((error) => {
        errorToastr("Error loading shifts from database");
        this.setState({ showLoader: false });
        this.getProcessedShift(this.state.dataState);
      });
  };

  parseTimeString = (timeString) => {
    try {
      // If it's already a Date object, return it
      if (timeString instanceof Date) return timeString;

      // If null or undefined, return null
      if (!timeString) return null;

      // Create a new date object for today
      const date = new Date();

      // Handle ISO format or contains 'T'
      if (typeof timeString === "string" && timeString.includes("T")) {
        return new Date(timeString);
      }

      // Handle "HH:MM:SS" or "HH:MM" format
      if (typeof timeString === "string") {
        const timeParts = timeString.split(":");
        if (timeParts.length >= 2) {
          const hours = parseInt(timeParts[0], 10);
          const minutes = parseInt(timeParts[1], 10);
          const seconds = timeParts.length > 2 ? parseInt(timeParts[2], 10) : 0;

          date.setHours(hours, minutes, seconds, 0);
          return date;
        }
      }

      // If we couldn't parse it, return the original date
      return date;
    } catch (error) {
      return new Date(); // Return current time as fallback
    }
  };

  formatTimeForSaving = (date) => {
    if (!date) return null;
    return toLocalDateTime(date);
  };

  reloadDefaultShifts = () => {
    // Clear existing shifts
    this.setState(
      {
        shifts: [],
        validationError: null,
        showLoader: true,
      },
      () => {
        // Only fetch default shifts if we have all required fields
        if (
          this.props.locationId &&
          this.props.departmentId &&
          this.props.positionId
        ) {
          // Fetch new default shifts
          this.fetchDefaultShifts(this.state.dataState);
        } else {
          // If we're missing any required fields, show validation message
          let missingFields = [];
          if (!this.props.locationId) missingFields.push(LocalizationHelper.getLocalizedTitle(LocalizationResource.Location_Label, "Location"));
          if (!this.props.departmentId) missingFields.push(LocalizationHelper.getLocalizedTitle(LocalizationResource.Department_Label, "Department"));
          if (!this.props.positionId) missingFields.push("Position");

          this.setState({
            validationError: `Please select ${missingFields.join(
              ", "
            )} before creating shifts.`,
            showLoader: false,
          });
        }
      }
    );
  };

  updateShiftsWithPositionDetails = (
    capacity: number,
    startDate: Date,
    endDate: Date
  ) => {
    // Update all shifts with new capacity and dates
    const updatedShifts = this.state.shifts.map((shift) => {
      const updatedShift = { ...shift };

      // Always update capacity
      updatedShift.capacity = capacity;

      // Update startDate if provided and current startDate is null/undefined
      if (startDate && !shift.startDate) {
        updatedShift.startDate = new Date(startDate);
      }

      // Only update endDate if it's null/undefined in the current shift
      if (endDate && !shift.endDate) {
        updatedShift.endDate = new Date(endDate);
        // Mark as modified only if we're updating the endDate
        if (updatedShift.reqShiftId && !updatedShift.isNewShift) {
          updatedShift.isModified = true;
        }
      }

      return updatedShift;
    });

    // Update state with modified shifts
    this.setState(
      {
        shifts: updatedShifts,
        showLoader: false,
      },
      () => {
        // Refresh the grid
        this.getProcessedShift(this.state.dataState);
      }
    );
  };

  getShiftTypes = () => {
    getShiftTypesService().then((res) => {
      if (res.data) {
        this.setState({ shiftTypes: res.data });
      }
    });
  };

  render() {
    return (
      <div className="row mt-3 mt-md-0">
        <div className="container-fluid">
          <div className="cand-bill-rate" id="cand-bill-rate-responsive">
            <Grid
              style={{ height: "auto" }}
              sortable={true}
              onDataStateChange={this.onDataStateChange}
              pageable={{ pageSizes: true }}
              data={this.state.data}
              {...this.state.dataState}
              total={this.state.totalCount}
              detail={ViewMoreComponent}
              expandField="expanded"
              className="kendo-grid-custom k-widget lastchild"
              filterable={false}
              columnMenu={false}
            >
              <GridNoRecords>
                {GridNoRecord(this.state.showLoader)}
              </GridNoRecords>

              <GridColumn
                field="name"
                title="Shift Name"
                columnMenu={WithoutFilterColumnMenu}
                cell={(props) => CellRender(props, "Shift Name")}
              />
              <GridColumn
                field="shiftTypeId"
                title="Shift Type"
                columnMenu={WithoutFilterColumnMenu}
                cell={(props) => {
                  const shiftType = this.state.shiftTypes.find(
                    type => type.id === props.dataItem.shiftTypeId
                  );
                  return <td>{shiftType ? shiftType.name : ''}</td>;
                }}
              />
              <GridColumn
                field="capacity"
                title="No of Positions"
                columnMenu={WithoutFilterColumnMenu}
                cell={(props) => (
                  <td className="text-right">
                    {props.dataItem[props.field] || "-"}
                  </td>
                )}
              />
              <GridColumn
                field="startDate"
                filter="date"
                format="{0:d}"
                editor="date"
                title="Start Date"
                columnMenu={WithoutFilterColumnMenu}
                cell={(props) => CellRender(props, "Start Date")}
              />
              <GridColumn
                field="endDate"
                filter="date"
                format="{0:d}"
                editor="date"
                title="End Date"
                columnMenu={WithoutFilterColumnMenu}
                cell={(props) => CellRender(props, "End Date")}
              />

              <GridColumn
                field="startTime"
                title="Start Time"
                columnMenu={WithoutFilterColumnMenu}
                cell={(props) => {
                  // Create a custom cell renderer for time values
                  const timeValue = props.dataItem[props.field];
                  let displayValue = timeValue;

                  if (timeValue) {
                    displayValue = formatTimeForDisplay(timeValue);
                  }
                  return <td title={displayValue}>{displayValue || "-"}</td>;
                }}
              />
              <GridColumn
                field="endTime"
                title="End Time"
                columnMenu={WithoutFilterColumnMenu}
                cell={(props) => {
                  // Create a custom cell renderer for time values
                  const timeValue = props.dataItem[props.field];
                  let displayValue = timeValue;

                  if (timeValue) {
                    displayValue = formatTimeForDisplay(timeValue);
                  }

                  return <td title={displayValue}>{displayValue || "-"}</td>;
                }}
              />              
              <GridColumn
                title="Action"
                sortable={false}
                width="70px"
                cell={(props) => {
                  if (!props.dataItem) {
                    return <td></td>;
                  }
                  return (
                    <RowActions
                      dataItem={props.dataItem}
                      currentState={""}
                      rowId={props.dataItem.reqShiftId || ""}
                      handleClick={this.handleActionClick}
                      defaultActions={DefaultActions(props)}
                    />
                  );
                }}
                headerCell={() =>
                  CustomMenu(
                    this.state.totalCount,
                    this.GenerateShiftnModal.bind(this)
                  )
                }
              />
            </Grid>
          </div>
        </div>
        {this.state.showNewShiftModal && this.dataItem && (
          <div id="Edit-division">
            <Dialog className="col-12 For-all-responsive-height">
              <AddNewShift
                reqistionShiftId={this.dataItem.reqShiftId}
                targetStartDate={this.props.targetStartDate}
                targetEndDate={this.props.targetEndDate}
                dataItem={this.dataItem}
                onCloseModal={() =>
                  this.setState({ showNewShiftModal: false }, () => {
                    this.dataItem = undefined;
                    this.getProcessedShift(this.state.dataState);
                  })
                }
                onOpenModal={() => this.setState({ showNewShiftModal: true })}
                parent={this}
                positionId={this.props.positionId}
                positionName={this.props.positionName}
              />
            </Dialog>
          </div>
        )}
        {this.state.showEditModal && this.dataItem && (
          <div id="Edit-division">
            <Dialog className="col-12 For-all-responsive-height">
              <AddNewShift
                reqistionShiftId={this.dataItem.reqShiftId}
                targetStartDate={this.props.targetStartDate}
                targetEndDate={this.props.targetEndDate}
                dataItem={this.dataItem}
                onCloseModal={() =>
                  this.setState({ showEditModal: false }, () => {
                    this.dataItem = undefined;
                    this.getProcessedShift(this.state.dataState);
                  })
                }
                onOpenModal={() => this.setState({ showEditModal: true })}
                parent={this}
                positionId={this.props.positionId}
                positionName={this.props.positionName}
              />
            </Dialog>
          </div>
        )}
        {this.state.showDeleteModal && this.dataItem && (
          <div id="Edit-division">
            <ConfirmationModal
              message={REQ_SHIFT_DELETED_CONFIRM_MSG}
              showModal={this.state.showDeleteModal}
              handleYes={() => this.deleteShift(this.dataItem.deleteId)}
              handleNo={() => this.setState({ showDeleteModal: false })}
            />
          </div>
        )}
      </div>
    );
  }
}
export default ManageShifts;
